import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { isExistsLogin } from "../miscellaneous/handleCookies";
import { performLogout } from "../miscellaneous/performLogout";

const WARNING_MINUTES_BEFORE = 15;

interface UseScheduledLogoutOptions {
  logoutTime?: string; // HH:mm format, default "00:00"
  warningMinutes?: number;
}

function getTargetTime(logoutTime: string): dayjs.Dayjs {
  const [hour, minute] = logoutTime.split(":").map(Number);
  const now = dayjs();
  let target = now.hour(hour).minute(minute).second(0).millisecond(0);

  if (target.isBefore(now) || target.isSame(now)) {
    target = target.add(1, "day");
  }

  return target;
}

function getMsUntilTarget(logoutTime: string): number {
  return getTargetTime(logoutTime).diff(dayjs());
}

function getMsUntilWarning(logoutTime: string, warningMinutes: number): number {
  return getMsUntilTarget(logoutTime) - warningMinutes * 60 * 1000;
}

export function useScheduledLogout(
  options: UseScheduledLogoutOptions = {}
): { showWarningBanner: boolean } {
  const {
    logoutTime = "00:00",
    warningMinutes = WARNING_MINUTES_BEFORE,
  } = options;

  const [showWarningBanner, setShowWarningBanner] = useState<boolean>(() => {
    if (!isExistsLogin()) return false;
    return getMsUntilWarning(logoutTime, warningMinutes) <= 0;
  });

  useEffect(() => {
    if (!isExistsLogin()) return;

    // Compute the actual target once on mount/dep change
    const target = getTargetTime(logoutTime);
    const warningTime = target.subtract(warningMinutes, "minute");

    const scheduleTimers = () => {
      const now = dayjs();
      const msUntilTarget = target.diff(now);
      const msUntilWarning = warningTime.diff(now);

      let warningTimer: ReturnType<typeof setTimeout> | null = null;
      let logoutTimer: ReturnType<typeof setTimeout> | null = null;

      if (msUntilTarget <= 0) {
        // Target time already passed — logout immediately
        performLogout();
      } else if (msUntilWarning <= 0) {
        // Already in warning window
        setShowWarningBanner(true);
        logoutTimer = setTimeout(() => {
          performLogout();
        }, msUntilTarget);
      } else {
        warningTimer = setTimeout(() => {
          setShowWarningBanner(true);
        }, msUntilWarning);

        logoutTimer = setTimeout(() => {
          performLogout();
        }, msUntilTarget);
      }

      return { warningTimer, logoutTimer };
    };

    let timers = scheduleTimers();

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!isExistsLogin()) return;

      // Clear existing timers and re-evaluate against the fixed target
      if (timers.warningTimer !== null) clearTimeout(timers.warningTimer);
      if (timers.logoutTimer !== null) clearTimeout(timers.logoutTimer);

      timers = scheduleTimers();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (timers.warningTimer !== null) clearTimeout(timers.warningTimer);
      if (timers.logoutTimer !== null) clearTimeout(timers.logoutTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [logoutTime, warningMinutes]);

  return { showWarningBanner };
}
